import { useCallback, useEffect, useRef, useState } from 'react';
import { DocumentNode, FetchPolicy, TypedDocumentNode, useLazyQuery } from '@apollo/client';
import { ErrorPolicy, WatchQueryFetchPolicy } from '@apollo/client/core';
import { useDebounce } from '../../../forms/utils/useDebounce';

export type TypeaheadOption<T extends any = string> = {
  label: string;
  id: string;
  value: T;
  disabled?: boolean;
  code?: string;
  subtitle?: string;
  referenceId?: string;
  icon?: JSX.Element;
};

export type DefaultQueryVariables = { q: { search: string }; limit: number; offset: number };

interface Output<T, QueryData> {
  values: TypeaheadOption<T>[];
  filter(text: string): void;
  loading: boolean;
  data?: QueryData;
  /** Fetches the next page of results and appends them to `values`. No-op when there are no more pages. */
  loadMore(): void;
  /** Whether another page of results is available to load. */
  hasNextPage: boolean;
  /** Whether a `loadMore` request is currently in flight (distinct from the initial/filter `loading`). */
  loadingMore: boolean;
}

interface Options<SingleValue, QueryData, QueryVariables> {
  debounceTime: number;
  limit: number;
  offset: number;
  initialQuery: boolean;
  fetchPolicy?: FetchPolicy;
  nextFetchPolicy?: WatchQueryFetchPolicy;
  errorPolicy?: ErrorPolicy;
  mapValues: (result: QueryData) => TypeaheadOption<SingleValue>[];
  mapVariables?: (variables: DefaultQueryVariables) => QueryVariables;
  /**
   * Optional resolver for the total number of matching records. When provided, `hasNextPage` is derived by comparing
   * the number of loaded options against this total. When omitted, `hasNextPage` falls back to "the last page came
   * back full" (i.e. it returned exactly `limit` items).
   */
  mapTotalCount?: (result: QueryData) => number | undefined;
}

const defaultOptions: Options<any, any, any> = {
  initialQuery: true,
  debounceTime: 300,
  limit: 30,
  offset: 30,
  mapValues: () => []
};

// REQUIREMENT: INPUT MODEL needs to look like:  {q: {search: STRING}, limit: NUMBER, offset: NUMBER}. IF looks different then use mapVariables function
export function useApiTypeahead<
  SingleValue,
  QueryData = any,
  QueryVariables extends Record<string, any> = DefaultQueryVariables
>(
  query: DocumentNode | TypedDocumentNode<QueryData, QueryVariables>,
  options?: Partial<Options<SingleValue, QueryData, QueryVariables>>
): Output<SingleValue, QueryData> {
  const [config] = useState<Options<SingleValue, QueryData, QueryVariables>>({
    ...defaultOptions,
    ...options
  });

  const { initialQuery, mapVariables, limit } = config;

  const [get, { loading, data }] = useLazyQuery<QueryData, QueryVariables | { [prop: string]: any }>(query, {
    fetchPolicy: config.fetchPolicy,
    errorPolicy: config.errorPolicy,
    nextFetchPolicy: config.nextFetchPolicy
  });

  // Accumulated, page-by-page results. Replaced on a fresh search, appended on loadMore.
  const [values, setValues] = useState<TypeaheadOption<SingleValue>[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Refs mirror the latest search term / loaded count so loadMore always paginates from the current position
  // without needing to be re-created (and re-bound to react-window's infinite loader) on every render.
  const searchTermRef = useRef('');
  const loadedCountRef = useRef(0);
  const loadingMoreRef = useRef(false);

  const runQuery = useCallback(
    async (term: string, offset: number, append: boolean) => {
      const variables = { q: { search: term }, limit, offset };
      const result = await get({ variables: mapVariables?.(variables) ?? variables });
      const page = result.data ? config.mapValues(result.data) : [];

      setValues((current) => {
        const next = append ? current.concat(page) : page;
        loadedCountRef.current = next.length;

        const total = result.data ? config.mapTotalCount?.(result.data) : undefined;
        setHasNextPage(total !== undefined ? next.length < total : page.length === limit);

        return next;
      });
    },
    [get, limit, mapVariables, config]
  );

  useEffect(() => {
    if (initialQuery) {
      searchTermRef.current = '';
      loadedCountRef.current = 0;
      runQuery('', 0, false);
    }
  }, [initialQuery, runQuery]);

  const filterFn = useCallback(
    (term: string) => {
      searchTermRef.current = term;
      loadedCountRef.current = 0;
      runQuery(term, 0, false);
    },
    [runQuery]
  );

  const debouncedFilter = useDebounce(filterFn, config.debounceTime);

  const filter = useCallback(
    (text: string) => {
      debouncedFilter(text);
    },
    [debouncedFilter]
  );

  const loadMore = useCallback(() => {
    if (loadingMoreRef.current || !hasNextPage) {
      return;
    }

    loadingMoreRef.current = true;
    setLoadingMore(true);

    runQuery(searchTermRef.current, loadedCountRef.current, true).finally(() => {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    });
  }, [hasNextPage, runQuery]);

  return {
    values,
    filter,
    loading,
    data,
    loadMore,
    hasNextPage,
    loadingMore
  };
}
